import { Point } from 'content-creation-common/models';
import {
  combineParentAndChildTransforms,
  getElementTransformAtFrame,
  getMatrix,
} from 'content-creation-common/utils/transform';

import { DEFAULT_KEYFRAME_TRANSFORM } from '../features/creative/constants';
import { isNil } from './object';

export const removeChildTransforms = (flattenTransform, childTransform) => {
  const {
    alpha,
    rotation,
    scale: { x: scaleX, y: scaleY },
    translation: { x: translateX, y: translateY },
  } = flattenTransform;

  // Note, this order or operations matters see preview-canvas/snapPreview/imageData/README.md
  const flattenTransformMatrix = getMatrix(
    scaleX,
    scaleY,
    translateX,
    translateY,
    rotation,
  );

  const {
    alpha: childAlpha,
    rotation: childRotation,
    scale: { x: childScaleX, y: childScaleY },
    translation: { x: childTranslateX, y: childTranslateY },
  } = childTransform;

  // Note, this order or operations matters see preview-canvas/snapPreview/imageData/README.md
  const childTransformMatrix = getMatrix(
    childScaleX,
    childScaleY,
    childTranslateX,
    childTranslateY,
    childRotation,
  );

  // Divide the matrices to get translation coordinates.
  // Use math for alpha, rotation, and scale
  flattenTransformMatrix.divide(childTransformMatrix);
  const finalAlpha = alpha / childAlpha;
  const finalRotation = rotation - childRotation;
  const finalScale = {
    x: scaleX / childScaleX,
    y: scaleY / childScaleY,
  };
  const { translate: finalTranslation } = flattenTransformMatrix.decompose();

  return {
    alpha: finalAlpha,
    rotation: finalRotation,
    scale: finalScale,
    translation: finalTranslation,
  };
};

export const getCanvasRelativeElementTransformAtFrame = (
  elementId,
  frameNumber,
  useCurrent,
  elementGetter,
) => {
  const element = elementGetter(elementId);
  if (isNil(element)) return DEFAULT_KEYFRAME_TRANSFORM;

  const elementTransform = getElementTransformAtFrame(
    element,
    frameNumber,
    useCurrent,
  );

  const parentId = element.groupId;
  if (isNil(parentId)) return elementTransform;

  const parentTransform = getCanvasRelativeElementTransformAtFrame(
    parentId,
    frameNumber,
    useCurrent,
    elementGetter,
  );
  return combineParentAndChildTransforms(parentTransform, elementTransform);
};

export const getMediumSpaceTransform = (
  originalCanvasSpaceTransform,
  currentCanvasSpaceTransform,
) => {
  if (isNil(originalCanvasSpaceTransform)) return currentCanvasSpaceTransform;

  const scaleX =
    currentCanvasSpaceTransform.scale.x / originalCanvasSpaceTransform.scale.x;
  const scaleY =
    currentCanvasSpaceTransform.scale.y / originalCanvasSpaceTransform.scale.y;

  const x =
    (currentCanvasSpaceTransform.translation.x -
      originalCanvasSpaceTransform.translation.x) /
    originalCanvasSpaceTransform.scale.x;
  const y =
    (currentCanvasSpaceTransform.translation.y -
      originalCanvasSpaceTransform.translation.y) /
    originalCanvasSpaceTransform.scale.y;

  return {
    scale: {
      x: scaleX,
      y: scaleY,
    },
    translation: {
      x,
      y,
    },
  };
};

/**
 * @param {{alpha: number, translation: {x: number, y: number}, scale: {x: number, y: number}, rotation: number}} parent
 * @param {!Point} canvasSpacePosition
 * @returns {!Point}
 */
export const getRelativePosition = (parent, canvasSpacePosition) => {
  const relativePosition = new Point(
    (canvasSpacePosition.x - parent.translation.x) / parent.scale.x,
    (canvasSpacePosition.y - parent.translation.y) / parent.scale.y,
  );
  relativePosition.rotateAroundPoint(new Point(0, 0), -parent.rotation);
  return relativePosition;
};
